import { useMemo } from 'react';
import { requestPermissionFrom, TRequestPermission } from '../Permission/currentPermission';
import { useAPIPermissions } from '../Permission/hooks';
import { isRefusal } from './isRefusal';
import { useUser } from '../UserContext';

export type TUseRequestPermissionParams = {
  customerId?: string;
  organisationId?: string;
};

export type TUseRequestPermission = {
  /**
   * `true` until the lookup has settled.
   *
   * Separate from the answer rather than folded into it as a fourth value: the card withholds the
   * control while loading *and* while the answer is `unknown`, but it says nothing during loading
   * and does say something on `unknown`. One field that meant both would have to be unpicked at
   * every call site.
   */
  isResolving: boolean;
  requestPermission: TRequestPermission;
};

/**
 * Whether this organisation may request a check for this customer.
 *
 * **The signal is the permission the customer granted, not a probe of the action itself.** An
 * earlier revision inferred it from `GET /v1/task/customer/{id}` on the argument that the list and
 * the create call share one authorisation predicate — true, and still true. What that argument
 * cannot carry is the rule built on it. The list maps every task through
 * `generateTaskUrl.generate(...)` (`TaskResource.java:126-128`), which throws when a task has no
 * tracking reference (`GenerateTaskUrl.java:29`), so the call answers `500` for a data reason
 * **after** authorisation has already passed. Observed in production on 2026-08-26 alongside a
 * `200 Active` permission and a `POST` that succeeded: the old rule took the control away from a
 * caller who could use it. Settled by the owner the same day.
 *
 * **The read goes through `useAPIPermissions`, which is why this page asks once and not twice.**
 * `IndividualDetailsView` renders on the same page and needs the same rows, and VME-4156 asks for
 * one request, not two. Both now subscribe to the same react-query key, so the two surfaces
 * cannot disagree about a customer's permission because one of their two requests happened to
 * fail. A hand-rolled fetch here could not have delivered that at any length — the deduplication
 * lives in the shared cache, not in either caller.
 *
 * **The endpoint is reachable by the roles that see this card.** `PermissionResource.java:105` reads
 * `@RolesAllowed({"admin", "customer"})` and does not name `client`, which is not a barrier:
 * `customer` is the base role added to every user on creation
 * (`CognitoUserAttributesSynchronizer.java:155-176`), with `client` added on top, and roles resolve
 * from `cognito:groups`. Confirmed in production before it was explained in source.
 *
 * **Reads, never writes.** The hook answers one question and holds no request state; creating a task
 * and tracking outstanding ones stay in `useFaceCheckTasks`, whose failures are a separate subject
 * from whether the customer authorised anyone.
 *
 * **No `onError`.** A failed lookup is not a failure the client needs reporting: the card already
 * says it cannot tell, and a toast on page load for a condition the card is displaying would say
 * the same thing twice. Failures of the *action* still report, from the hook that performs it.
 *
 * **An admin is permitted without consulting the permission at all.** person-api authorises this
 * action through a chain — `admin` → `customer` who controls this customer → the client
 * organisation's permission — and only the last link is a question about an organisation. This hook
 * answers that last link, so applying its answer to an admin withheld a control from a caller whose
 * `POST` would have succeeded. The design said so before the code did: the implementation design's
 * evidence register records "Admin bypasses the permission lookup", the high-level design's Round 2
 * revision records 「门只对 client-only 用户成立」, and its acceptance matrix reads `admin allow`.
 * This is that sentence, finally implemented.
 *
 * **It does not close the whole gap, and the comment says so on purpose.** The middle link — a
 * caller holding both roles who controls this customer — cannot be answered here: `GET /v1/user/me`
 * returns `{ userName, roles }` and nothing about the caller's relationship to a customer. Nor can
 * the third link's controller bypass, because `GET /v1/permission?customerId=` returns only the
 * rows found by customer, while the backend predicate also consults the rows found by controller.
 * Both are backend work; the honest fix for all three is an eligibility endpoint this page can ask
 * directly. Until then this hook is stricter than person-api for those two populations, in the
 * fail-closed direction.
 */
export function useRequestPermission({
  customerId,
  organisationId,
}: TUseRequestPermissionParams): TUseRequestPermission {
  // Both ids identify the permission. A missing organisation cannot be matched against, and
  // `requestPermissionFrom` refuses it rather than matching rows whose own id is absent.
  const enabled = Boolean(customerId && organisationId);

  // Called unconditionally and its result read before anything else, so the identity a decision is
  // made against is never half-known.
  const { state: userState } = useUser();
  const isAdmin = userState.status === 'authenticated' && userState.isAdmin;
  const isIdentityResolving = userState.status === 'loading';

  const { permissions, isLoadingPermissions, loadPermissionsError } = useAPIPermissions(
    customerId ?? '',
    { enabled },
  );

  return useMemo(() => {
    // Before every other branch, including `enabled`. An admin's authorisation does not depend on
    // this customer's permission rows, on the organisation in the url, or on the lookup succeeding,
    // so none of those may withhold the control from them — a `403`, a `500` and a dropped
    // connection are all irrelevant to a caller person-api authorises by role.
    if (isAdmin) {
      return { isResolving: false, requestPermission: 'permitted' };
    }

    // Read before the loading flag, not after: a query held back by `enabled` still reports
    // `isLoadingPermissions: true`, and reporting that as "still waiting" would leave the card
    // withholding the control forever on a page that never had the ids to ask with.
    if (!enabled) {
      return { isResolving: false, requestPermission: 'unknown' };
    }

    if (isLoadingPermissions) {
      return { isResolving: true, requestPermission: 'unknown' };
    }

    // **A `403` is an answer, not a failure to get one.** person-api refuses this read through
    // `clientUserHasPermissionToCustomer` — the same predicate that refuses the `POST` this card's
    // button makes. So a `403` here is not "we could not tell"; it is the backend saying, about this
    // caller and this customer, exactly what the request control would have been told.
    //
    // **Under this identity model, not as a guarantee the endpoint gives.** The resource also
    // carries `@RolesAllowed({"admin","customer"})`, which could in principle answer `403` on roles
    // alone; it does not in practice because `customer` is added to every user unconditionally at
    // creation, so that gate passes for anyone signed in and the refusal that arrives is the
    // predicate's. If that ever stops being true there is a second kind of `403` and this mapping
    // needs revisiting. Treating it
    // as unknown was a drift introduced when the signal moved to this endpoint: D-20 had already
    // settled that a `403` gets the permission notice and that `500`/`404` get nothing, and D-45
    // changed which endpoint is read, not what a `403` from it means.
    //
    // Every other failure stays `unknown`, and for the original reason: a `401`, a `500` and a
    // dropped connection differ in cause and agree in consequence — none of them told us whether
    // this customer authorised this organisation. A failed query also carries no data, and the
    // shared hook renders no data as `[]`, which would classify as `notPermitted` on the strength of
    // an empty array rather than an answer.
    //
    // The residual, accepted when this endpoint was chosen: a customer that does not exist also
    // fails the predicate and so also answers `403`, and reads here as "not authorised". A client
    // reaching this page from their own customer list cannot arrive at a customer that is not there.
    const fromPermission: TRequestPermission = loadPermissionsError
      ? isRefusal(loadPermissionsError)
        ? 'notPermitted'
        : 'unknown'
      : requestPermissionFrom(permissions, organisationId);

    // **A live permission is enough on its own, and waiting on the identity here would be a
    // regression of its own.** D-45's rule is that a confirmed `Active` permission renders the
    // control; being an admin is a second, independent reason to render it, never a precondition
    // for the first. `/v1/user/me` has no timeout and no fallback — it leaves `loading` only when
    // its promise settles — so consulting the identity before honouring a permission that already
    // said yes would withhold the control indefinitely from exactly the caller the design is about.
    if (fromPermission === 'permitted') {
      return { isResolving: false, requestPermission: 'permitted' };
    }

    // Only here does the identity change the answer, so only here is it worth waiting for. The
    // permission would withhold the control; whether that silence is also a *statement* depends on
    // whether this caller is an admin, and saying "this customer has not authorised your
    // organisation" before knowing would draw a notice and replace it a moment later — the
    // appearing-then-vanishing notice this card's own rule against speaking mid-flight prevents.
    if (isIdentityResolving) {
      return { isResolving: true, requestPermission: 'unknown' };
    }

    return { isResolving: false, requestPermission: fromPermission };
  }, [
    isAdmin,
    isIdentityResolving,
    enabled,
    isLoadingPermissions,
    loadPermissionsError,
    permissions,
    organisationId,
  ]);
}
